/* eslint-disable react-hooks/exhaustive-deps */
import React, { useContext, useEffect, useMemo, useState } from "react";
import moment from "moment";
import CurrencyFormat from "react-currency-format";
import jsPDF from "jspdf";
import "jspdf-autotable";
import Swal from "sweetalert2";
import axios from "axios";
import authHeader from "../../UserServices/auth-header";
import MenuButton from "../MenuButton/MenuButton";
import UserServices from "../../UserServices/UserServices";
import { NoteState } from "../../context/notes/NoteState";
import useTable from "../../hooks/useTable";
import useSortableData from "../../hooks/useSortableData";

const parseNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const todayValue = () => new Date().toLocaleDateString("en-CA");

const hasStaffNote = (note) => {
  const normalized = `${note || ""}`.trim();
  return normalized !== "" && normalized !== "N/A";
};

const getOrderReceived = (row) =>
  (row.payment_method || []).reduce((sum, payment) => sum + parseNumber(payment.received), 0);

const getOrderPending = (row) =>
  (row.payment_method || []).reduce((sum, payment) => sum + parseNumber(payment.pending), 0);

const getPaymentTypes = (row) =>
  [...new Set((row.payment_method || []).map((payment) => payment.payment_type).filter(Boolean))].join(", ");

const getDisplayName = (row) =>
  hasStaffNote(row.note) ? `${row.note}`.split(",")[0].trim() : row.name;

const getSaleType = (row) => {
  const pending = getOrderPending(row);
  const discount = parseNumber(row.discount);

  if (pending > 0 && hasStaffNote(row.note)) {
    return "management";
  }

  if (hasStaffNote(row.note) && discount > 0) {
    return "staff";
  }

  if (hasStaffNote(row.note)) {
    return "staff";
  }

  return "regular";
};

const flattenTransactionResponse = (responseData) => {
  const groups = responseData?.data || [];
  const rows = [];

  groups.forEach((group) => {
    (group.data || []).forEach((order) => {
      rows.push({
        name: order.client_name,
        client_id: order.client_id,
        branch_id: order.branch_id,
        branch_name: order.branch_name,
        payment_method: order.payment_history || [],
        items: order.items || [],
        net_price: parseNumber(order.net_price),
        price: parseNumber(order.price),
        date: order.date,
        sold_by: order.sold_by,
        order_id: order.id,
        tax: parseNumber(order.tax),
        discount: parseNumber(order.discount),
        note: order.note,
      });
    });
  });

  return rows;
};

const getRowItemMetrics = (row, itemId) => {
  const items = itemId
    ? (row.items || []).filter((item) => `${item.package_id}` === `${itemId}`)
    : row.items || [];

  if (!items.length) {
    return null;
  }

  return {
    items,
    price: items.reduce((sum, item) => sum + parseNumber(item.price), 0),
    discount: items.reduce((sum, item) => sum + parseNumber(item.discount), 0),
    tax: items.reduce((sum, item) => sum + parseNumber(item.tax), 0),
    net_price: items.reduce((sum, item) => sum + parseNumber(item.net_price), 0),
  };
};

const SummaryCard = ({ label, value }) => (
  <div className="col-12 col-md-6 col-xl-3">
    <div className="card border-0 shadow-sm h-100">
      <div className="card-body">
        <div className="text-muted small mb-1">{label}</div>
        <CurrencyFormat
          value={value}
          displayType="text"
          thousandSeparator
          prefix="Rs "
          suffix=" /-"
          renderText={(formatted) => <div className="fw-semibold fs-5">{formatted}</div>}
        />
      </div>
    </div>
  </div>
);

export default function DetailedCafeReport() {
  const { state } = useContext(NoteState);
  const { branch_id: contextBranchId } = state;

  const [loading, setLoading] = useState(false);
  const [dateLoading, setDateLoading] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [limit, setLimit] = useState("25");
  const [page, setPage] = useState(1);
  const [exportingExcel, setExportingExcel] = useState(false);
  const [expandedOrderId, setExpandedOrderId] = useState(null);

  const [branches, setBranches] = useState([]);
  const [itemsList, setItemsList] = useState([]);
  const [clientsList, setClientsList] = useState([]);
  const [staffList, setStaffList] = useState([]);

  const [rawRows, setRawRows] = useState([]);
  const [apiTotals, setApiTotals] = useState({
    total_price: 0,
    total_discount: 0,
    total_net_price: 0,
  });

  const [filters, setFilters] = useState({
    branch_id: `${contextBranchId || ""}`,
    sdate: todayValue(),
    edate: todayValue(),
    item_id: "",
    client_id: "",
    staff_name: "",
    payment_type: "",
    sale_type: "",
    order_id: "",
    sold_by: "",
  });

  useEffect(() => {
    setFilters((prev) => ({
      ...prev,
      branch_id: `${contextBranchId || prev.branch_id || ""}`,
    }));
  }, [contextBranchId]);

  useEffect(() => {
    const loadLookups = async () => {
      try {
        const [branchRes, itemRes, clientRes, staffRes, managementRes] = await Promise.all([
          UserServices.getBranches(),
          filters.branch_id
            ? UserServices.getActiveCafeProducts(filters.branch_id)
            : Promise.resolve({ data: { data: { data: [] } } }),
          filters.branch_id
            ? UserServices.getCafeClient(filters.branch_id, "1")
            : Promise.resolve({ data: { data: { data: [] } } }),
          filters.branch_id
            ? UserServices.staffNameforCafe(filters.branch_id, "no")
            : Promise.resolve({ data: { data: [] } }),
          filters.branch_id
            ? UserServices.staffNameforCafe(filters.branch_id, "yes")
            : Promise.resolve({ data: { data: [] } }),
        ]);

        setBranches(branchRes?.data?.data?.data || branchRes?.data?.data || []);
        setItemsList(itemRes?.data?.data?.data || itemRes?.data?.data || []);
        setClientsList(clientRes?.data?.data?.data || clientRes?.data?.data || []);

        const staffRows = [
          ...(staffRes?.data?.data || []),
          ...(managementRes?.data?.data || []),
        ].map((person) => ({
          id: person.id,
          name: `${person.first_name || ""} ${person.last_name || ""}`.trim(),
        }));

        const uniqueStaff = [];
        const seen = new Set();
        staffRows.forEach((person) => {
          if (!person.name || seen.has(person.name)) {
            return;
          }
          seen.add(person.name);
          uniqueStaff.push(person);
        });
        setStaffList(uniqueStaff);
      } catch (error) {
        console.error(error);
      }
    };

    loadLookups();
  }, [filters.branch_id]);

  const paymentOptions = useMemo(() => {
    const values = new Set();
    rawRows.forEach((row) => {
      (row.payment_method || []).forEach((payment) => {
        if (payment.payment_type) {
          values.add(payment.payment_type);
        }
      });
    });
    return [...values].sort();
  }, [rawRows]);

  const soldByOptions = useMemo(() => {
    const values = new Set();
    rawRows.forEach((row) => {
      if (row.sold_by) {
        values.add(row.sold_by);
      }
    });
    return [...values].sort();
  }, [rawRows]);

  const filteredRows = useMemo(() => {
    return rawRows
      .map((row) => {
        const metrics = getRowItemMetrics(row, filters.item_id);
        if (filters.item_id && !metrics) {
          return null;
        }

        return {
          ...row,
          display_name: getDisplayName(row),
          sale_type: getSaleType(row),
          received: getOrderReceived(row),
          pending: getOrderPending(row),
          payment_types: getPaymentTypes(row),
          item_metrics: metrics,
          price: metrics ? metrics.price : row.price,
          discount: metrics ? metrics.discount : row.discount,
          tax: metrics ? metrics.tax : row.tax,
          net_price: metrics ? metrics.net_price : row.net_price,
          is_staff_sale: getSaleType(row) === "staff",
          is_management_sale: getSaleType(row) === "management",
        };
      })
      .filter(Boolean)
      .filter((row) => {
        if (filters.staff_name && !`${row.note || ""}`.toLowerCase().includes(filters.staff_name.toLowerCase())) {
          return false;
        }

        if (filters.payment_type && !`${row.payment_types}`.includes(filters.payment_type)) {
          return false;
        }

        if (filters.sale_type && row.sale_type !== filters.sale_type) {
          return false;
        }

        if (filters.sold_by && row.sold_by !== filters.sold_by) {
          return false;
        }

        return true;
      });
  }, [rawRows, filters.item_id, filters.staff_name, filters.payment_type, filters.sale_type, filters.sold_by]);

  const summary = useMemo(() => {
    const totals = filteredRows.reduce(
      (acc, row) => {
        acc.orders += 1;
        acc.gross += parseNumber(row.price);
        acc.discount += parseNumber(row.discount);
        acc.staff_discount += row.is_staff_sale ? parseNumber(row.discount) : 0;
        acc.management_discount += row.is_management_sale ? parseNumber(row.discount) : 0;
        acc.gst += parseNumber(row.tax);
        acc.net += parseNumber(row.net_price);
        acc.received += parseNumber(row.received);
        acc.pending += parseNumber(row.pending);
        return acc;
      },
      {
        orders: 0,
        gross: 0,
        discount: 0,
        staff_discount: 0,
        management_discount: 0,
        gst: 0,
        net: 0,
        received: 0,
        pending: 0,
      }
    );

    totals.without_gst = totals.gross - totals.discount;
    totals.with_gst = totals.net;
    return totals;
  }, [filteredRows]);

  const exportLink = useMemo(() => {
    if (!filters.branch_id || !filters.sdate || !filters.edate) {
      return "";
    }

    const params = new URLSearchParams({
      branch_id: filters.branch_id,
      start_date: filters.sdate,
      end_date: filters.edate,
      category: "10",
    });

    if (filters.client_id) {
      params.append("client_id", filters.client_id);
    }

    if (filters.order_id) {
      params.append("order_id", filters.order_id);
    }

    return `${process.env.REACT_APP_API_Link}/orders/export-transaction?${params.toString()}`;
  }, [filters]);

  const setQuickDates = async (preset) => {
    setDateLoading(true);
    try {
      const response = await UserServices.getStartEndDates(preset);
      if (response.status === 200) {
        setFilters((prev) => ({
          ...prev,
          sdate: response.data.StartDate,
          edate: response.data.EndDate,
        }));
      }
    } catch (error) {
      console.error(error);
    } finally {
      setDateLoading(false);
    }
  };

  const handleFilterChange = (event) => {
    const { name, value } = event.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
    setPage(1);
  };

  const generateReport = async () => {
    if (Date.parse(filters.sdate) > Date.parse(filters.edate)) {
      Swal.fire({
        icon: "error",
        title: "Invalid date range",
        text: "End date should be greater than or equal to start date.",
      });
      return;
    }

    if (!filters.branch_id) {
      Swal.fire({
        icon: "error",
        title: "Branch required",
        text: "Please select a branch.",
      });
      return;
    }

    setLoading(true);
    setShowResults(false);
    setExpandedOrderId(null);

    try {
      const response = await UserServices.getCafeTransactionReport(filters.branch_id, {
        sdate: filters.sdate,
        edate: filters.edate,
        category: "10",
        client_id: filters.client_id || undefined,
        order_id: filters.order_id || undefined,
      });

      if (response.status === 200) {
        setRawRows(flattenTransactionResponse(response.data));
        setApiTotals({
          total_price: parseNumber(response.data.total_price),
          total_discount: parseNumber(response.data.total_discount),
          total_net_price: parseNumber(response.data.total_net_price),
        });
        setShowResults(true);
        setPage(1);
      }
    } catch (error) {
      console.error(error);
      setRawRows([]);
      setApiTotals({ total_price: 0, total_discount: 0, total_net_price: 0 });
      setShowResults(true);

      if (error.response?.status === 401) {
        Swal.fire({
          icon: "error",
          title: "Session expired",
          text: "Please log in again.",
          confirmButtonColor: "#e72e2e",
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const downloadExcel = async () => {
    if (!exportLink) {
      Swal.fire({
        icon: "info",
        title: "Generate report first",
        text: "Please generate the report before exporting Excel.",
      });
      return;
    }

    setExportingExcel(true);
    try {
      const response = await axios.get(exportLink, {
        headers: authHeader(),
        responseType: "blob",
      });
      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `detailed-cafe-report-${filters.sdate}-${filters.edate}.xlsx`;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error(error);
      Swal.fire({
        icon: "error",
        title: "Excel export failed",
        text: "Could not download Excel file. Please try again.",
      });
    } finally {
      setExportingExcel(false);
    }
  };

  const generatePdf = () => {
    const doc = new jsPDF("l", "mm", "a4");
    doc.setFontSize(14);
    doc.text("Detailed Cafe Report", 14, 12);
    doc.setFontSize(9);
    doc.text(
      `Branch: ${branches.find((b) => `${b.id}` === `${filters.branch_id}`)?.name || filters.branch_id} | ${filters.sdate} to ${filters.edate}`,
      14,
      18
    );

    doc.autoTable({
      startY: 24,
      head: [["Client/Staff", "Order", "Date", "Price", "Discount", "GST", "Net", "Received", "Pending", "Payment", "Sold By"]],
      body: filteredRows.map((row) => [
        row.display_name,
        row.order_id,
        moment(row.date).format("DD-MM-YYYY"),
        row.price,
        row.discount,
        row.tax,
        row.net_price,
        row.received,
        row.pending,
        row.payment_types,
        row.sold_by,
      ]),
      styles: { fontSize: 7 },
      headStyles: { fillColor: [232, 0, 43] },
    });

    doc.save(`detailed-cafe-report-${filters.sdate}-${filters.edate}.pdf`);
  };

  const { slice } = useTable(filteredRows, page, limit);
  const { items, requestSort, sortConfig } = useSortableData(slice);

  const getClassNamesFor = (name) => {
    if (!sortConfig) {
      return undefined;
    }
    return sortConfig.key === name ? sortConfig.direction : undefined;
  };

  return (
    <div className="wrapper">
      <main className="page-content customer-contnent">
        <div className="site-main-design">
          <MenuButton />
          <div className="card">
            <div className="card-header py-3">
              <div className="row align-items-center">
                <div className="col-md-8">
                  <h5 className="mb-0">Detailed Cafe Report</h5>
                  <small className="text-muted">
                    Branch, date, item, client, staff, payment and sale-type filters with full cafe totals.
                  </small>
                </div>
              </div>
            </div>

            <div className="card-body">
              <div className="row g-3">
                <div className="col-12 col-md-3">
                  <label className="form-label">Branch</label>
                  <select className="form-select" name="branch_id" value={filters.branch_id} onChange={handleFilterChange}>
                    <option value="">Select Branch</option>
                    {branches.map((branch) => (
                      <option key={branch.id} value={branch.id}>
                        {branch.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="col-12 col-md-3">
                  <label className="form-label">Start Date</label>
                  <input className="form-control" type="date" name="sdate" value={filters.sdate} onChange={handleFilterChange} />
                </div>

                <div className="col-12 col-md-3">
                  <label className="form-label">End Date</label>
                  <input className="form-control" type="date" name="edate" value={filters.edate} onChange={handleFilterChange} />
                </div>

                <div className="col-12 col-md-3">
                  <label className="form-label">Order ID</label>
                  <input className="form-control" name="order_id" value={filters.order_id} onChange={handleFilterChange} placeholder="Optional" />
                </div>

                <div className="col-12 col-md-3">
                  <label className="form-label">Item</label>
                  <select className="form-select" name="item_id" value={filters.item_id} onChange={handleFilterChange}>
                    <option value="">All Items</option>
                    {itemsList.map((item) => (
                      <option key={item.id} value={item.id}>
                        {item.name || item.package_name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="col-12 col-md-3">
                  <label className="form-label">Client</label>
                  <select className="form-select" name="client_id" value={filters.client_id} onChange={handleFilterChange}>
                    <option value="">All Clients</option>
                    {clientsList.map((client) => (
                      <option key={client.id} value={client.id}>
                        {`${client.first_name || ""} ${client.last_name || ""}`.trim()}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="col-12 col-md-3">
                  <label className="form-label">Staff / Management</label>
                  <select className="form-select" name="staff_name" value={filters.staff_name} onChange={handleFilterChange}>
                    <option value="">All Staff</option>
                    {staffList.map((staff) => (
                      <option key={`${staff.id}-${staff.name}`} value={staff.name}>
                        {staff.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="col-12 col-md-3">
                  <label className="form-label">Sale Type</label>
                  <select className="form-select" name="sale_type" value={filters.sale_type} onChange={handleFilterChange}>
                    <option value="">All Sales</option>
                    <option value="regular">Regular Client Sale</option>
                    <option value="staff">Staff Sale</option>
                    <option value="management">Management Pending</option>
                  </select>
                </div>

                <div className="col-12 col-md-3">
                  <label className="form-label">Payment Type</label>
                  <select className="form-select" name="payment_type" value={filters.payment_type} onChange={handleFilterChange}>
                    <option value="">All Payments</option>
                    {paymentOptions.map((payment) => (
                      <option key={payment} value={payment}>
                        {payment}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="col-12 col-md-3">
                  <label className="form-label">Sold By</label>
                  <select className="form-select" name="sold_by" value={filters.sold_by} onChange={handleFilterChange}>
                    <option value="">All Users</option>
                    {soldByOptions.map((seller) => (
                      <option key={seller} value={seller}>
                        {seller}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="col-12">
                  <div className="d-flex flex-wrap gap-2">
                    {[
                      ["Last Month", "Last Month"],
                      ["Last Quarter", "Last Quarter"],
                      ["Yesterday", "Yesterday"],
                      ["To-Date", "To-Date"],
                      ["Previous 30 days", "Previous 30 days"],
                      ["Previous 90 days", "Previous 90 days"],
                    ].map(([label, preset]) => (
                      <button
                        key={preset}
                        type="button"
                        className="btn btn-outline-secondary btn-sm"
                        disabled={dateLoading}
                        onClick={() => setQuickDates(preset)}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="col-12">
                  <button type="button" className="btn btn-primary px-4" onClick={generateReport} disabled={loading}>
                    {loading ? "Loading..." : "Generate Report"}
                  </button>
                </div>
              </div>

              {showResults ? (
                <>
                  <hr className="my-4" />

                  <div className="row g-3 mb-4">
                    <div className="col-12 col-md-6 col-xl-3">
                      <div className="card border-0 shadow-sm h-100">
                        <div className="card-body">
                          <div className="text-muted small mb-1">Total Orders</div>
                          <div className="fw-semibold fs-5">{summary.orders}</div>
                        </div>
                      </div>
                    </div>
                    <SummaryCard label="Gross Sales (Before Discount)" value={summary.gross} />
                    <SummaryCard label="Sales Without GST" value={summary.without_gst} />
                    <SummaryCard label="Total Discount" value={summary.discount} />
                    <SummaryCard label="Staff Discounts" value={summary.staff_discount} />
                    <SummaryCard label="Management Discounts" value={summary.management_discount} />
                    <SummaryCard label="Total GST" value={summary.gst} />
                    <SummaryCard label="Net Sales (With GST)" value={summary.with_gst} />
                    <SummaryCard label="Total Received" value={summary.received} />
                    <SummaryCard label="Total Pending" value={summary.pending} />
                  </div>

                  <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
                    <div>
                      <h6 className="mb-0 text-uppercase">Filtered Results ({filteredRows.length})</h6>
                      <small className="text-muted">
                        API totals for selected range: Gross Rs {apiTotals.total_price} | Discount Rs {apiTotals.total_discount} | Net Rs {apiTotals.total_net_price}
                      </small>
                    </div>
                    <div className="d-flex gap-2">
                      <button type="button" className="btn btn-primary btn-sm" onClick={downloadExcel} disabled={exportingExcel || !exportLink}>
                        <i className="bi bi-download" /> {exportingExcel ? "Downloading..." : "Excel"}
                      </button>
                      <button type="button" className="btn btn-primary btn-sm" onClick={generatePdf}>
                        <i className="bi bi-file-earmark-pdf" /> PDF
                      </button>
                    </div>
                  </div>

                  <div className="row mb-3">
                    <div className="col-md-2 ms-auto">
                      <select className="form-select" value={limit} onChange={(event) => setLimit(event.target.value)}>
                        <option value="25">25</option>
                        <option value="50">50</option>
                        <option value="100">100</option>
                        <option value="500">500</option>
                        <option value={filteredRows.length || "1000"}>All</option>
                      </select>
                    </div>
                  </div>

                  <div className="table-responsive">
                    <table className="table table-borderless table-style-1 table-striped" id="detailed-cafe-report-table">
                      <thead>
                        <tr>
                          <th>Sr#</th>
                          <th onClick={() => requestSort("display_name")} className={getClassNamesFor("display_name")}>Client / Staff</th>
                          <th onClick={() => requestSort("branch_name")} className={getClassNamesFor("branch_name")}>Branch</th>
                          <th onClick={() => requestSort("order_id")} className={getClassNamesFor("order_id")}>Order ID</th>
                          <th onClick={() => requestSort("date")} className={getClassNamesFor("date")}>Sale Date</th>
                          <th onClick={() => requestSort("sale_type")} className={getClassNamesFor("sale_type")}>Sale Type</th>
                          <th onClick={() => requestSort("price")} className={getClassNamesFor("price")}>Price</th>
                          <th onClick={() => requestSort("discount")} className={getClassNamesFor("discount")}>Discount</th>
                          <th onClick={() => requestSort("tax")} className={getClassNamesFor("tax")}>GST</th>
                          <th onClick={() => requestSort("net_price")} className={getClassNamesFor("net_price")}>Net Price</th>
                          <th onClick={() => requestSort("received")} className={getClassNamesFor("received")}>Received</th>
                          <th onClick={() => requestSort("pending")} className={getClassNamesFor("pending")}>Pending</th>
                          <th>Payment</th>
                          <th onClick={() => requestSort("sold_by")} className={getClassNamesFor("sold_by")}>Sold By</th>
                        </tr>
                      </thead>
                      <tbody>
                        {items.length ? (
                          items.map((row, index) => {
                            const rowItems = row.item_metrics?.items || row.items || [];
                            const serial = page === 1 ? index + 1 : (page - 1) * Number(limit) + index + 1;

                            return (
                              <React.Fragment key={`${row.order_id}-${index}`}>
                                <tr>
                                  <td>
                                    <div className="d-flex align-items-center gap-2">
                                      <span>{serial}</span>
                                      {rowItems.length ? (
                                        <button
                                          type="button"
                                          className="btn btn-link btn-sm p-0"
                                          onClick={() =>
                                            setExpandedOrderId((current) =>
                                              current === row.order_id ? null : row.order_id
                                            )
                                          }
                                        >
                                          <i className={`bi ${expandedOrderId === row.order_id ? "bi-chevron-up" : "bi-chevron-down"}`} />
                                        </button>
                                      ) : null}
                                    </div>
                                  </td>
                                  <td>{row.display_name}</td>
                                  <td>{row.branch_name}</td>
                                  <td>{row.order_id}</td>
                                  <td>{moment(row.date).format("DD-MM-YYYY")}</td>
                                  <td className="text-capitalize">{row.sale_type}</td>
                                  <td>
                                    <CurrencyFormat value={row.price} displayType="text" thousandSeparator prefix="Rs " suffix=" /-" />
                                  </td>
                                  <td>
                                    <CurrencyFormat value={row.discount} displayType="text" thousandSeparator prefix="Rs " suffix=" /-" />
                                  </td>
                                  <td>
                                    <CurrencyFormat value={row.tax} displayType="text" thousandSeparator prefix="Rs " suffix=" /-" />
                                  </td>
                                  <td>
                                    <CurrencyFormat value={row.net_price} displayType="text" thousandSeparator prefix="Rs " suffix=" /-" />
                                  </td>
                                  <td>
                                    <CurrencyFormat value={row.received} displayType="text" thousandSeparator prefix="Rs " suffix=" /-" />
                                  </td>
                                  <td>
                                    <CurrencyFormat value={row.pending} displayType="text" thousandSeparator prefix="Rs " suffix=" /-" />
                                  </td>
                                  <td>{row.payment_types || "-"}</td>
                                  <td>{row.sold_by || "-"}</td>
                                </tr>

                                {expandedOrderId === row.order_id && rowItems.length ? (
                                  <tr>
                                    <td colSpan="14" className="bg-light">
                                      <table className="table table-sm mb-0">
                                        <thead>
                                          <tr>
                                            <th>Item</th>
                                            <th>Price</th>
                                            <th>Discount</th>
                                            <th>GST</th>
                                            <th>Net Price</th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {rowItems.map((item, itemIndex) => (
                                            <tr key={`${row.order_id}-item-${itemIndex}`}>
                                              <td>{item.package_name}</td>
                                              <td>{item.price}</td>
                                              <td>{item.discount}</td>
                                              <td>{item.tax}</td>
                                              <td>{item.net_price}</td>
                                            </tr>
                                          ))}
                                        </tbody>
                                      </table>
                                    </td>
                                  </tr>
                                ) : null}
                              </React.Fragment>
                            );
                          })
                        ) : (
                          <tr>
                            <td colSpan="14" className="text-center">No record found</td>
                          </tr>
                        )}
                      </tbody>
                      {items.length ? (
                        <tfoot>
                          <tr>
                            <td colSpan="6" className="text-end fw-semibold">Totals</td>
                            <td className="fw-semibold">
                              <CurrencyFormat value={summary.gross} displayType="text" thousandSeparator prefix="Rs " suffix=" /-" />
                            </td>
                            <td className="fw-semibold">
                              <CurrencyFormat value={summary.discount} displayType="text" thousandSeparator prefix="Rs " suffix=" /-" />
                            </td>
                            <td className="fw-semibold">
                              <CurrencyFormat value={summary.gst} displayType="text" thousandSeparator prefix="Rs " suffix=" /-" />
                            </td>
                            <td className="fw-semibold">
                              <CurrencyFormat value={summary.net} displayType="text" thousandSeparator prefix="Rs " suffix=" /-" />
                            </td>
                            <td className="fw-semibold">
                              <CurrencyFormat value={summary.received} displayType="text" thousandSeparator prefix="Rs " suffix=" /-" />
                            </td>
                            <td className="fw-semibold">
                              <CurrencyFormat value={summary.pending} displayType="text" thousandSeparator prefix="Rs " suffix=" /-" />
                            </td>
                            <td colSpan="2" />
                          </tr>
                        </tfoot>
                      ) : null}
                    </table>
                  </div>

                  {filteredRows.length > Number(limit) ? (
                    <div className="d-flex justify-content-end mt-3">
                      <nav>
                        <ul className="pagination mb-0">
                          <li className={`page-item ${page === 1 ? "disabled" : ""}`}>
                            <button type="button" className="page-link" onClick={() => setPage((current) => Math.max(1, current - 1))}>
                              Previous
                            </button>
                          </li>
                          <li className="page-item disabled">
                            <span className="page-link">Page {page}</span>
                          </li>
                          <li
                            className={`page-item ${
                              page * Number(limit) >= filteredRows.length ? "disabled" : ""
                            }`}
                          >
                            <button type="button" className="page-link" onClick={() => setPage((current) => current + 1)}>
                              Next
                            </button>
                          </li>
                        </ul>
                      </nav>
                    </div>
                  ) : null}
                </>
              ) : null}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
